import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { ONE_HOUR_IN_MS } from '@careerstart/wae-common/src/main/constants/timeConversions';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Grid, Typography } from '@mui/material';

import asyncService from '../../../../../datahub/asyncService';
import { millisecondsToHours } from '../../../../../main/utils/timeConverter';
import WaeButton from '../../../../components/Button';
import FormTextField from '../../../../components/Form/FormTextField';
import selectUser from '../../../../store/selectors/appSelector';
import theme from '../../../../theme';
import { showSnackbar } from '../../../app';
import { updateCorpMaxWeeklyHoursScheduledPerWorker } from '../reducer';

import { validateMaxWeeklyHoursScheduledPerWorker } from './FormData/FormFieldDataSettings';

const rootBoxStyle = { width: '100%' };

const sectionHeaderStyle = {
  fontFamily: theme.components.detailView.typography.sectionHeader.fontFamily,
  fontSize: theme.components.detailView.typography.sectionHeader.fontSize,
  color: theme.components.detailView.typography.sectionHeader.fontColor,
};

const contentGridStyle = { padding: theme.spacing(3, 0, 3) };

const semiBoldTextStyle = {
  fontFamily: theme.components.detailView.typography.semiBoldText.fontFamily,
  fontSize: theme.components.detailView.typography.semiBoldText.fontSize,
  color: theme.components.detailView.typography.semiBoldText.fontColor,
};

const hoursFromMs = (ms) =>
  typeof ms === 'number' && Number.isFinite(ms) ? Math.round(millisecondsToHours(ms)) : undefined;

const Settings = ({ corporation }) => {
  const dispatch = useDispatch();
  const { token, refreshToken } = useSelector(selectUser);

  const savedHours = hoursFromMs(corporation?.maxWeeklyHoursScheduledPerWorker);

  const [isEditing, setIsEditing] = useState(false);
  const [hoursValue, setHoursValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const error = validateMaxWeeklyHoursScheduledPerWorker(hoursValue);

  const handleEditClick = () => {
    setHoursValue(String(savedHours ?? ''));
    setIsEditing(true);
  };

  const handleSave = () => {
    if (error) return;

    const newMaxWeeklyHoursScheduledPerWorker = Number(hoursValue) * ONE_HOUR_IN_MS;

    setIsSaving(true);
    asyncService({
      httpMethod: 'POST',
      route: 'corporations/set-max-weekly-hours-scheduled-per-worker',
      data: {
        corporationId: corporation.id,
        maxWeeklyHoursScheduledPerWorker: newMaxWeeklyHoursScheduledPerWorker,
      },
      token,
      refreshToken,
      dispatch,
      onSuccess: () => {
        setIsSaving(false);
        setIsEditing(false);
        dispatch(
          updateCorpMaxWeeklyHoursScheduledPerWorker({
            corporationId: corporation.id,
            maxWeeklyHoursScheduledPerWorker: newMaxWeeklyHoursScheduledPerWorker,
          })
        );
        dispatch(
          showSnackbar({ message: 'corporation.setMaxWeeklyHoursScheduledPerWorker.success' })
        );
      },
      onError: () => {
        setIsSaving(false);
        dispatch(showSnackbar({ message: 'error.corporation.failUpdate' }));
      },
    });
  };

  const input = {
    name: 'maxWeeklyHoursScheduledPerWorker',
    value: hoursValue,
    onChange: (event) => setHoursValue(event.target.value),
    onBlur: () => {},
  };

  const meta = { error };

  return (
    <Box sx={rootBoxStyle}>
      <Typography sx={sectionHeaderStyle}>
        {LanguageConverterV2('corporation.settings.tabLabel')}
      </Typography>
      <Grid container alignItems="center" gap={3} sx={contentGridStyle}>
        {isEditing ? (
          <>
            <FormTextField
              input={input}
              meta={meta}
              labelKey="corporation.setMaxWeeklyHoursScheduledPerWorker.label"
              fullWidth
            />
            <WaeButton
              text={LanguageConverterV2('buttonText.save')}
              onClick={handleSave}
              disabled={!!error || isSaving}
              showIndeterminateProgress={isSaving}
            />
          </>
        ) : (
          <>
            <Typography sx={semiBoldTextStyle}>
              {LanguageConverterV2('corporation.setMaxWeeklyHoursScheduledPerWorker.label')}:{' '}
              {savedHours ?? '—'}
            </Typography>
            <WaeButton
              text={LanguageConverterV2('button.edit')}
              onClick={handleEditClick}
              disabled={isSaving}
            />
          </>
        )}
      </Grid>
    </Box>
  );
};

Settings.propTypes = {
  corporation: PropTypes.shape({
    id: PropTypes.string,
    maxWeeklyHoursScheduledPerWorker: PropTypes.number,
  }),
};

export default Settings;
